import { useRef, useState, useCallback } from 'react'
import { Upload, X, FileText, Image, Loader2 } from 'lucide-react'
import { Button } from '../Button'
import type { DocumentType, ExpenseDocument } from '@/api/client'
import { api } from '@/api/client'
import { Analytics } from '@/analytics/events'

const ALLOWED_TYPES = [
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'
]
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT = '.png,.jpg,.jpeg,.webp,.heic,.pdf'

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  Receipt: 'Receipt',
  ProofOfPayment: 'Proof of Payment',
  LetterOfMedicalNecessity: 'Letter of Medical Necessity',
  ExplanationOfBenefits: 'Explanation of Benefits',
  Other: 'Other',
}

interface PendingFile {
  file: File
  documentType: DocumentType
  description: string
  error?: string
  uploading: boolean
  uploaded: boolean
}

interface DocumentUploaderProps {
  expenseId: string
  onUploadComplete: (doc: ExpenseDocument) => void
  requiresLMN?: boolean
}

export function DocumentUploader({ expenseId, onUploadComplete, requiresLMN }: DocumentUploaderProps) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | undefined => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `File type '${file.type || 'unknown'}' is not supported. Use PNG, JPG, WEBP, HEIC, or PDF.`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds 10MB limit.`
    }
    return undefined
  }

  const addFiles = useCallback((files: FileList | File[]) => {
    const newPending: PendingFile[] = Array.from(files).map(file => ({
      file,
      documentType: 'Receipt' as DocumentType,
      description: '',
      error: validateFile(file),
      uploading: false,
      uploaded: false,
    }))
    setPendingFiles(prev => [...prev, ...newPending])
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }, [addFiles])

  const removePending = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  const updatePending = (index: number, updates: Partial<PendingFile>) => {
    setPendingFiles(prev => prev.map((p, i) => i === index ? { ...p, ...updates } : p))
  }

  const uploadFile = async (index: number) => {
    const pending = pendingFiles[index]
    if (pending.error || pending.uploading || pending.uploaded) return

    updatePending(index, { uploading: true })
    try {
      const doc = await api.uploadDocument(
        expenseId,
        pending.file,
        pending.documentType,
        pending.description || undefined
      )
      updatePending(index, { uploading: false, uploaded: true })
      if (doc) {
        Analytics.documentUploaded(pending.documentType, pending.file.type)
        onUploadComplete(doc)
      }
    } catch (err) {
      updatePending(index, {
        uploading: false,
        error: err instanceof Error ? err.message : 'Upload failed'
      })
    }
  }

  const uploadAll = async () => {
    for (let i = 0; i < pendingFiles.length; i++) {
      const p = pendingFiles[i]
      if (!p.error && !p.uploading && !p.uploaded) {
        await uploadFile(i)
      }
    }
  }

  const pendingCount = pendingFiles.filter(p => !p.error && !p.uploaded).length
  const fileIcon = (type: string) =>
    type.startsWith('image/') ? <Image size={16} className="text-[#718096]" /> : <FileText size={16} className="text-[#718096]" />

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#1B365D] bg-[#F7FAFC]' : 'border-[#E2E8F0] hover:border-[#1B365D]'
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <Upload size={20} className="mx-auto text-[#1B365D] mb-2" />
        <div className="text-[13px] text-[#4A5568]">Drop files here or click to upload</div>
        <div className="text-[11px] text-[#A0AEC0] mt-1">PNG, JPG, WEBP, HEIC, or PDF up to 10MB</div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {/* Pending files */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          {pendingFiles.map((pending, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                pending.error ? 'border-red-200 bg-red-50' :
                pending.uploaded ? 'border-[#10B981]/30 bg-[#10B981]/5' :
                'border-[#E2E8F0] bg-white'
              }`}
            >
              <div className="mt-0.5">{fileIcon(pending.file.type)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#1B365D] truncate">{pending.file.name}</div>
                <div className="text-[11px] text-[#A0AEC0]">
                  {(pending.file.size / 1024).toFixed(0)} KB
                </div>
                {pending.error && (
                  <div className="text-[11px] text-red-600 mt-1">{pending.error}</div>
                )}
                {pending.uploaded && (
                  <div className="text-[11px] text-[#10B981] mt-1">Uploaded</div>
                )}
                {!pending.error && !pending.uploaded && (
                  <div className="flex items-center gap-2 mt-2">
                    <select
                      className="text-[12px] border border-[#E2E8F0] rounded px-2 py-1 text-[#4A5568]"
                      value={pending.documentType}
                      onChange={(e) => updatePending(index, { documentType: e.target.value as DocumentType })}
                    >
                      {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <input
                      className="text-[12px] border border-[#E2E8F0] rounded px-2 py-1 text-[#4A5568] flex-1"
                      placeholder="Description (optional)"
                      value={pending.description}
                      onChange={(e) => updatePending(index, { description: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {pending.uploading && <Loader2 size={16} className="text-[#1B365D] animate-spin" />}
                {!pending.uploaded && (
                  <button
                    onClick={() => removePending(index)}
                    className="text-[#A0AEC0] hover:text-red-500 p-1"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {pendingCount > 0 && (
            <Button
              variant="primary"
              size="small"
              onClick={uploadAll}
            >
              Upload {pendingCount} {pendingCount === 1 ? 'file' : 'files'}
            </Button>
          )}
        </div>
      )}

      {requiresLMN && (
        <div className="text-[11px] text-[#C9A227] bg-[#C9A227]/10 rounded px-3 py-2">
          This expense category requires a Letter of Medical Necessity (LMN).
        </div>
      )}
    </div>
  )
}
